/**
 * CompactDegradeNotice — shown INSTEAD of the editing controls when the cursor
 * statement is a shape the compact surface can't safely edit (CTE, set op,
 * window function, subquery, heavier DML). A prominent headline naming the
 * REAL construct (CTE names, the actual set operator spelled per engine,
 * the real window expression, INSERT/upsert/MERGE facts — built by
 * `compactDegrade.ts`'s `classifyCompactDegrade` via `compactDegradeDetail.ts`'s
 * `buildDegradeDetail`) plus a short facts list, and a single "Open full
 * editor" call-to-action, so the user is never faced with broken, misleading,
 * or codename-only controls.
 *
 * `message` carries the rich detail newline-joined (`[headline, ...facts]`) so
 * this component's prop contract stays exactly `{ reason, message, onOpenFull }`
 * — the headline is always `message`'s first line; any remaining lines render
 * as a short facts list below it.
 *
 * spec: docs/superpowers/specs/2026-06-14-vqe-ux-refinement-design.md § WS3
 *   ("GRACEFUL DEGRADE"); Ralph/VqePolish/advisors/02-dml-safety-and-polish.md
 *   § B2.
 */

import { ExternalLink, Info } from 'lucide-react';

import { Button } from '../../../../../components/ui/button';
import type { CompactDegradeReason } from '../compactDegrade';
import { vqeCopy } from '../../ux/vqeCopy';

export interface CompactDegradeNoticeProps {
  readonly reason: CompactDegradeReason;
  /** Headline, optionally followed by `\n`-joined supporting facts. */
  readonly message: string;
  readonly onOpenFull: () => void;
}

export function CompactDegradeNotice({ reason, message, onOpenFull }: CompactDegradeNoticeProps) {
  const [headline, ...facts] = message.split('\n');
  return (
    <div
      className="flex flex-col gap-3 rounded-md border border-border bg-muted/30 p-3"
      data-testid="vqe-compact-degrade"
      data-degrade-reason={reason}
    >
      <div className="flex flex-col gap-1">
        <p className="flex items-start gap-2 text-muted-foreground" data-testid="vqe-compact-degrade-message">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>{headline}</span>
        </p>
        {facts.length > 0 ? (
          <ul
            className="ml-[22px] list-disc space-y-0.5 text-[11px] text-muted-foreground"
            data-testid="vqe-compact-degrade-facts"
          >
            {facts.map((fact, i) => (
              <li key={`${i}-${fact}`} data-testid={`vqe-compact-degrade-fact-${i}`}>
                {fact}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
      <Button
        type="button"
        size="sm"
        className="h-7 gap-1 self-start px-3 text-xs"
        onClick={onOpenFull}
        data-testid="vqe-compact-degrade-open-full"
      >
        <ExternalLink className="h-3.5 w-3.5" />
        {vqeCopy.actions.openFullEditor}
      </Button>
    </div>
  );
}
